import {Enquiries} from './Enquiries';
import {ContextDrawer} from '../ui/ContextDrawer';
import {ConfirmationDialog} from '../ui/ConfirmationDialog';
import {Icon} from '../ui/Icon';
import {useEffect,useRef,useState} from 'react';
import type {FormEvent} from 'react';
import {Link,useNavigate,useBlocker,useLocation} from 'react-router';
import {api} from '../api';
import {isApiError} from '../api-error';
import {DateOnlyInput} from '../components/DateOnlyInput';
import {
  buildingTypes,
  contractStatuses,
  enquirySources,
  hrbStatuses,
  optionalNumber,
  optionalString,
  prospectiveClientTypes,
  type DuplicateSuggestion,
  type Enquiry,
  type EnquiryOptions,
} from '../enquiries';
import {ErrorSummary,type ErrorSummaryItem,type ErrorSummarySource} from '../ui/ErrorSummary';
import {FieldMessage} from '../ui/FieldMessage';
import {FormSection} from '../ui/FormSection';
import {ErrorState,Loading} from '../ui/State';

type Assignee={id:string;display_name:string};
type CreateResponse={enquiry:Enquiry;duplicateSuggestions:DuplicateSuggestion[]};
type ValidationControl=HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement;
type FormFeedback={
  source:ErrorSummarySource;
  title:string;
  items:ErrorSummaryItem[];
  focusKey:number;
};

const SAVE_MESSAGE_ID='new-enquiry-save-message';
const validationFields={
  receivedDate:{controlId:'new-enquiry-received-date',label:'Received date'},
  residentialUnits:{controlId:'new-enquiry-residential-units',label:'Residential units'},
  commercialUnits:{controlId:'new-enquiry-commercial-units',label:'Commercial units'},
  primaryContactEmail:{controlId:'new-enquiry-primary-contact-email',label:'Email'},
  preferredManagementStartDate:{controlId:'new-enquiry-preferred-start-date',label:'Preferred management start date'},
  estimatedAnnualValue:{controlId:'new-enquiry-estimated-value',label:'Estimated annual management value (£)'},
} as const;
type ValidationField=keyof typeof validationFields;

function validationField(control:ValidationControl):ValidationField|undefined{
  const name=control.dataset.validationField??control.name;
  return Object.hasOwn(validationFields,name)?name as ValidationField:undefined;
}

function usefulValidationMessage(control:ValidationControl,field:ValidationField){
  const {label}=validationFields[field];
  if(control.validity.valueMissing)return `${label} is required.`;
  if(control.validity.typeMismatch)return 'Enter a valid email address.';
  if(control.validity.patternMismatch)return `Enter ${label.toLocaleLowerCase('en-GB')} in the correct format.`;
  if(control.validity.rangeUnderflow)return `${label} must be 0 or more.`;
  if(control.validity.stepMismatch){
    return field==='estimatedAnnualValue'
      ? 'Use no more than two decimal places.'
      : `${label} must be a whole number.`;
  }
  if(control.validity.badInput)return `Enter a number for ${label.toLocaleLowerCase('en-GB')}.`;
  return control.validationMessage||`${label} needs attention.`;
}

function clientValidationItems(form:HTMLFormElement):ErrorSummaryItem[]{
  const items:ErrorSummaryItem[]=[];
  const seen=new Set<ValidationField>();
  for(const control of Array.from(form.elements)){
    if(!(control instanceof HTMLInputElement||control instanceof HTMLSelectElement||control instanceof HTMLTextAreaElement)||control.validity.valid)continue;
    const field=validationField(control);
    if(!field||seen.has(field))continue;
    seen.add(field);
    const definition=validationFields[field];
    items.push({targetId:definition.controlId,label:definition.label,message:usefulValidationMessage(control,field)});
  }
  return items;
}

function saveFailureFeedback(cause:unknown,focusKey:number):FormFeedback{
  if(isApiError(cause)&&cause.code==='validation_failed'){
    return {
      source:'server',
      title:'The server could not validate this enquiry',
      items:[{targetId:SAVE_MESSAGE_ID,label:'Server validation',message:`${cause.message} Your entries remain in this form.`}],
      focusKey,
    };
  }
  if(isApiError(cause)&&cause.statusCode===0){
    return {
      source:'network',
      title:'The enquiry could not be sent',
      items:[{targetId:SAVE_MESSAGE_ID,label:'Network connection',message:'Check your connection and try again. Your entries remain in this form.'}],
      focusKey,
    };
  }
  const message=isApiError(cause)?cause.message:cause instanceof Error?cause.message:'The enquiry could not be saved.';
  return {
    source:'save',
    title:'The enquiry could not be saved',
    items:[{targetId:SAVE_MESSAGE_ID,label:'Save enquiry',message:`${message} Your entries remain in this form.`}],
    focusKey,
  };
}

function NewEnquiryForm({returnTo}:{returnTo:string}){
  const[dirty,setDirty]=useState(false),saved=useRef(false);
  const blocker=useBlocker(()=>dirty&&!saved.current);
  const navigate=useNavigate();
  const[assignees,setAssignees]=useState<Assignee[]>([]);
  const[canManageEnquiries,setCanManageEnquiries]=useState(false);
  const[optionsLoaded,setOptionsLoaded]=useState(false);
  const[saving,setSaving]=useState(false);
  const[loadError,setLoadError]=useState<unknown>();
  const[feedback,setFeedback]=useState<FormFeedback>();
  const createIdempotencyKey=useRef(crypto.randomUUID());
  const feedbackSequence=useRef(0);

  useEffect(()=>{api<EnquiryOptions>('/api/enquiries/options').then(value=>{
    setCanManageEnquiries(value.canManageEnquiries);
    setAssignees(value.assignees.map(owner=>({id:owner.id,display_name:owner.display_name??owner.email??owner.id})));
  }).catch(setLoadError).finally(()=>setOptionsLoaded(true))},[]);

  function handleInvalid(event:FormEvent<HTMLFormElement>){
    event.preventDefault();
    const items=clientValidationItems(event.currentTarget);
    if(!items.length)return;
    setFeedback({source:'client',title:'Check the highlighted fields',items,focusKey:++feedbackSequence.current});
  }

  function handleInput(event:FormEvent<HTMLFormElement>){
    const target=event.target;
    if(!(target instanceof HTMLInputElement||target instanceof HTMLSelectElement||target instanceof HTMLTextAreaElement))return;
    const field=validationField(target);
    if(!field)return;
    const targetId=validationFields[field].controlId;
    queueMicrotask(()=>{
      if(!target.isConnected)return;
      setFeedback(current=>{
        if(current?.source!=='client'||!current.items.some(item=>item.targetId===targetId))return current;
        if(!target.validity.valid){
          const items=current.items.map(item=>item.targetId===targetId
            ?{...item,message:usefulValidationMessage(target,field)}
            :item);
          return {...current,items};
        }
        const items=current.items.filter(item=>item.targetId!==targetId);
        return items.length?{...current,items}:undefined;
      });
    });
  }

  async function create(event:FormEvent<HTMLFormElement>){
    event.preventDefault();
    const form=new FormData(event.currentTarget);
    setSaving(true);
    setFeedback(undefined);
    const payload={
      receivedDate:optionalString(form,'receivedDate'),source:optionalString(form,'source'),assignedOwner:optionalString(form,'assignedOwner'),
      buildingName:optionalString(form,'buildingName'),addressLine1:optionalString(form,'addressLine1'),addressLine2:optionalString(form,'addressLine2'),townCity:optionalString(form,'townCity'),county:optionalString(form,'county'),postcode:optionalString(form,'postcode'),countryCode:optionalString(form,'countryCode'),
      residentialUnits:optionalNumber(form,'residentialUnits'),commercialUnits:optionalNumber(form,'commercialUnits'),buildingType:optionalString(form,'buildingType'),hrbStatus:optionalString(form,'hrbStatus'),
      prospectiveClientType:optionalString(form,'prospectiveClientType'),prospectiveClientName:optionalString(form,'prospectiveClientName'),primaryContactName:optionalString(form,'primaryContactName'),primaryContactEmail:optionalString(form,'primaryContactEmail'),primaryContactTelephone:optionalString(form,'primaryContactTelephone'),
      currentManagingAgent:optionalString(form,'currentManagingAgent'),preferredManagementStartDate:optionalString(form,'preferredManagementStartDate'),estimatedAnnualValue:optionalNumber(form,'estimatedAnnualValue'),proposedMailboxAlias:optionalString(form,'proposedMailboxAlias'),sourceReference:optionalString(form,'sourceReference'),notes:optionalString(form,'notes'),contractStatus:optionalString(form,'contractStatus'),
      reason:'Enquiry recorded through the Front Door workspace',idempotencyKey:createIdempotencyKey.current,
    };
    try{
      const response=await api<CreateResponse>('/api/enquiries',{method:'POST',body:JSON.stringify(payload)});
      saved.current=true;
      navigate(`/enquiries/${response.enquiry.id}`);
    }catch(cause){
      setFeedback(saveFailureFeedback(cause,++feedbackSequence.current));
    }finally{
      setSaving(false);
    }
  }

  const issueFor=(field:ValidationField)=>feedback?.source==='client'
    ?feedback.items.find(item=>item.targetId===validationFields[field].controlId)
    :undefined;
  const errorProps=(field:ValidationField)=>{
    const issue=issueFor(field);
    return {
      'aria-invalid':issue?true:undefined,
      'aria-describedby':issue?`${validationFields[field].controlId}-message`:undefined,
    } as const;
  };
  const inlineError=(field:ValidationField)=>{
    const issue=issueFor(field);
    return issue?<FieldMessage id={`${validationFields[field].controlId}-message`} tone="error">{issue.message}</FieldMessage>:null;
  };

  if(!optionsLoaded&&!loadError)return <Loading/>;
  if(!optionsLoaded)return <ErrorState error={loadError}/>;
  if(!canManageEnquiries)return <><Link className="back-link" to="/enquiries"><Icon name="arrow-left"/> Leads</Link><header className="page-heading enquiry-heading"><div><span className="eyebrow">Front Door</span><h1>Read-only enquiry access</h1><p>You can review enquiries, but your role cannot create or change them.</p></div></header></>;
  return <>
    <form className="enquiry-create-form" onChange={()=>setDirty(true)} onSubmit={create} onInvalid={handleInvalid} onInput={handleInput}>
      {feedback&&<ErrorSummary title={feedback.title} items={feedback.items} source={feedback.source} focusKey={feedback.focusKey}/>}
      {feedback&&feedback.source!=='client'&&<FieldMessage id={SAVE_MESSAGE_ID} className="enquiry-create-form__save-message" tone="error" focusable>{feedback.items[0]?.message}</FieldMessage>}
      <FormSection title="Lead details"><div className="enquiry-form-grid">
<label>Building or block name<input name="buildingName"/></label>
<label>Postcode<input name="postcode"/></label>
<div className="enquiry-create-form__field">
            <label htmlFor={validationFields.residentialUnits.controlId}>Residential units</label>
            <input {...errorProps('residentialUnits')} id={validationFields.residentialUnits.controlId} data-validation-field="residentialUnits" name="residentialUnits" type="number" min="0" step="1"/>
            {inlineError('residentialUnits')}
          </div>
<label>Prospective client or entity<input name="prospectiveClientName"/></label>
<label>Primary contact name<input name="primaryContactName"/></label>
<div className="enquiry-create-form__field">
            <label htmlFor={validationFields.primaryContactEmail.controlId}>Email</label>
            <input {...errorProps('primaryContactEmail')} id={validationFields.primaryContactEmail.controlId} data-validation-field="primaryContactEmail" name="primaryContactEmail" type="email"/>
            {inlineError('primaryContactEmail')}
          </div>
<label>Telephone<input name="primaryContactTelephone" type="tel"/></label>
<label>Source<input name="source" list="new-enquiry-source-options" placeholder="Unknown / not recorded"/><datalist id="new-enquiry-source-options">{enquirySources.map(value=><option key={value} value={value}/>)}</datalist></label>
<label>MYFM owner<select name="assignedOwner" defaultValue=""><option value="">Unassigned</option>{assignees.map(owner=><option key={owner.id} value={owner.id}>{owner.display_name}</option>)}</select></label>
</div></FormSection>
<details className="lead-additional-details"><summary>Additional details</summary>
<FormSection title="Enquiry intake">
        <div className="enquiry-form-grid">
          <div className="enquiry-create-form__field">
            <label htmlFor={validationFields.receivedDate.controlId}>Received date</label>
            <DateOnlyInput id={validationFields.receivedDate.controlId} data-validation-field="receivedDate" name="receivedDate" aria-describedby="new-enquiry-received-date-hint"/>
            <FieldMessage id="new-enquiry-received-date-hint">Leave blank if the received date is not known.</FieldMessage>
          </div>


          <label>Source message, form or reference<input name="sourceReference"/></label>
        </div>
      </FormSection>
      <FormSection title="Building information" description={<p>These are enquiry facts only.</p>}>
        <div className="enquiry-form-grid">
          <label>Address line 1<input name="addressLine1"/></label><label>Address line 2<input name="addressLine2"/></label><label>Town or city<input name="townCity"/></label><label>County<input name="county"/></label>
          <label>Country code<input name="countryCode" maxLength={2}/></label>

          <div className="enquiry-create-form__field">
            <label htmlFor={validationFields.commercialUnits.controlId}>Commercial units</label>
            <input {...errorProps('commercialUnits')} id={validationFields.commercialUnits.controlId} data-validation-field="commercialUnits" name="commercialUnits" type="number" min="0" step="1"/>
            {inlineError('commercialUnits')}
          </div>
          <label>Building type<select name="buildingType" defaultValue=""><option value="">Unknown / not recorded</option>{buildingTypes.map(value=><option key={value}>{value}</option>)}</select></label>
          <label>HRB status<select name="hrbStatus" defaultValue=""><option value="">Unknown / not recorded</option>{hrbStatuses.map(value=><option key={value}>{value}</option>)}</select></label>
        </div>
      </FormSection>
      <FormSection title="Prospective client">
        <div className="enquiry-form-grid">
          <label>Client type<input name="prospectiveClientType" list="new-enquiry-client-type-options" placeholder="Unknown / not recorded"/><datalist id="new-enquiry-client-type-options">{prospectiveClientTypes.map(value=><option key={value} value={value}/>)}</datalist></label>


          <label>Current managing agent<input name="currentManagingAgent"/></label>
        </div>
      </FormSection>
      <FormSection title="Commercial context">
        <div className="enquiry-form-grid">
          <div className="enquiry-create-form__field">
            <label htmlFor={validationFields.preferredManagementStartDate.controlId}>Preferred management start date</label>
            <DateOnlyInput id={validationFields.preferredManagementStartDate.controlId} data-validation-field="preferredManagementStartDate" name="preferredManagementStartDate"/>
          </div>
          <div className="enquiry-create-form__field">
            <label htmlFor={validationFields.estimatedAnnualValue.controlId}>Estimated annual management value (£)</label>
            <input {...errorProps('estimatedAnnualValue')} id={validationFields.estimatedAnnualValue.controlId} data-validation-field="estimatedAnnualValue" name="estimatedAnnualValue" type="number" min="0" step="0.01"/>
            {inlineError('estimatedAnnualValue')}
          </div>
          <label>Contract status<select name="contractStatus" defaultValue=""><option value="">Unknown / not recorded</option>{contractStatuses.map(value=><option key={value}>{value}</option>)}</select></label><label>Proposed mailbox alias<input name="proposedMailboxAlias"/></label>
          <label className="span-all">Notes<textarea name="notes" rows={5}/></label>
        </div>
      </FormSection>
      </details>
<div className="enquiry-submit"><button type="submit" disabled={saving}>{saving?'Saving…':'Save lead'}</button><Link className="button-link secondary" to={returnTo} state={{focusNewLead:true}}>Cancel</Link></div>
    </form>
    <ConfirmationDialog open={blocker.state==='blocked'} title="Discard unsaved lead?" description={<p>Your unsaved lead details will be discarded.</p>} confirmLabel="Discard lead" cancelLabel="Keep editing" busyLabel="Saving…" tone="danger" busy={saving} onConfirm={()=>{saved.current=true;if(blocker.state==='blocked')blocker.proceed()}} onCancel={()=>{if(blocker.state==='blocked')blocker.reset()}}/>
  </>;
}

export function NewEnquiry(){
 const navigate=useNavigate(),location=useLocation();
 const candidate=location.state?.leadReturn?.to;
 let returnTo='/enquiries';
 if(typeof candidate==='string'&&candidate.startsWith('/enquiries')&&!candidate.startsWith('//')){
  const url=new URL(candidate,'https://staff.invalid');
  if(url.origin==='https://staff.invalid'&&url.pathname==='/enquiries'&&!url.hash&&[...url.searchParams.keys()].every(key=>['q','stage','source','assignedOwner','followUp','lane','limit'].includes(key)))returnTo=url.pathname+url.search;
 }
 const close=()=>navigate(returnTo,{state:{focusNewLead:true}});
 return <><Enquiries viewSearch={new URL(returnTo,'https://staff.invalid').search}/><ContextDrawer className="creation-drawer" onClose={close} labelledBy="create-lead-title">
  <header className="creation-drawer__heading"><h1 id="create-lead-title">New lead</h1><button type="button" aria-label="Close new lead" onClick={close}><Icon name="x" size={20}/></button></header>
  <NewEnquiryForm returnTo={returnTo}/>
 </ContextDrawer></>;
}
